import React from "react";
import PropTypes from "prop-types";
import { SyncIcon } from "./icons/SyncIcon";
import { CheckCircleIcon } from "./icons/CheckCircleIcon";
import { ErrorIcon } from "./icons/ErrorIcon";
import { TextLink } from "./TextLink";

const PUBLISH_SPINNER_CSS = `
  @keyframes publish-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .publish-spinner {
    animation: publish-spin 2s linear infinite;
  }
`;

const baseInnerStyle = {
  padding: "16px 20px",
  display: "flex",
  alignItems: "center",
  gap: 12,
  borderRadius: 4,
  margin: "16px 0"
};

const STATUS_COLORS = {
  publishing: "#c6dff5",
  complete: "#d1e6ce",
  error: "#f5c6c6"
};

export function PublishStatusBanner({ status, onViewDetails, onCancel, onClose, onTryAgain, onDismiss, detailsReady }) {
  const isComplete = status === "complete";
  const isError = status === "error";
  const innerStyle = { ...baseInnerStyle, backgroundColor: STATUS_COLORS[status] || STATUS_COLORS.publishing };

  let icon;
  let message;
  if (isError) {
    icon = <ErrorIcon size={24} fill="#c62828" />;
    message = "There was an error during publishing.";
  } else if (isComplete) {
    icon = <CheckCircleIcon size={24} fill="#467d3e" />;
    message = "Publish complete";
  } else {
    icon = <SyncIcon size={24} fill="#4599df" className="publish-spinner" />;
    message = "Publish currently underway...";
  }

  /*
  While the publish is in flight but the API hasn't yet pushed a
  history entry the CMS can fetch, there is no concrete publishId to
  open a details dialog for. Show passive "Acquiring details..." text
  in place of the link until the entry materializes. Only relevant in
  the "publishing" state -- complete/error always have details, except
  for the soft-error case (saga or watchdog) where the publish never
  produced a history entry and the parent omits onViewDetails.
  */
  const showAcquiringDetails = status === "publishing" && detailsReady === false;
  const showViewDetailsLink = !showAcquiringDetails && typeof onViewDetails === "function";

  return (
    <div className="publish-status-banner" style={{ padding: "65px 75px" }}>
      {status === "publishing" && <style>{PUBLISH_SPINNER_CSS}</style>}
      <div className="inner" style={innerStyle}>
        {icon}
        <div className="status-text" style={{ flex: 1, fontSize: 14 }}>
          {message}
        </div>
        <div className="links" style={{ display: "flex", gap: 16, alignItems: "center" }}>
          {showAcquiringDetails && (
            <span style={{ fontSize: 14, color: "#666", fontStyle: "italic" }}>
              Acquiring details...
            </span>
          )}
          {showViewDetailsLink && (
            <TextLink onClick={onViewDetails}>View details</TextLink>
          )}
          {isError ? [
              <TextLink key="try-again" onClick={onTryAgain}>Try again</TextLink>,
              <TextLink key="dismiss" onClick={onDismiss}>Dismiss</TextLink>
          ] : isComplete ? (
            <TextLink onClick={onClose}>Close</TextLink>
          ) : null
          // TODO: Cancel button removed from UI. The onCancel handler currently
          // only writes metadata to the last step in publish history — it does
          // not actually halt a publish in progress. Before re-enabling, we need
          // a robust server-side mechanism to abort an active publish event.
          }
        </div>
      </div>
    </div>
  );
}

PublishStatusBanner.propTypes = {
  status: PropTypes.oneOf(["publishing", "complete", "error"]),
  onViewDetails: PropTypes.func,
  onCancel: PropTypes.func,
  onClose: PropTypes.func,
  onTryAgain: PropTypes.func,
  onDismiss: PropTypes.func,
  detailsReady: PropTypes.bool
};

PublishStatusBanner.defaultProps = {
  status: "publishing",
  detailsReady: true
};
